import { useState, useEffect, useCallback, useRef } from 'react'
import useSWR from 'swr'
import { apiFetch } from '@/utils'

interface UseInfiniteScrollOptions {
  endpoint: string
  limit?: number
  sort?: 'asc' | 'desc'
}

interface UseInfiniteScrollResult<T> {
  items: T[]
  isLoading: boolean
  isLoadingMore: boolean
  error: Error | null
  hasMore: boolean
  sentinelRef: React.RefObject<HTMLDivElement>
  refresh: () => void
}

export function useInfiniteScroll<T>({
  endpoint,
  limit = 10,
  sort = 'desc'
}: UseInfiniteScrollOptions): UseInfiniteScrollResult<T> {
  const [items, setItems] = useState<T[]>([])
  const [page, setPage] = useState(1)
  const [hasMore, setHasMore] = useState(true)
  const [isLoadingMore, setIsLoadingMore] = useState(false)
  const sentinelRef = useRef<HTMLDivElement>(null)

  // Build URL with pagination params
  const url = `${endpoint}?page=${page}&limit=${limit}&sort=${sort}`

  // Fetch current page
  const { data, error, isLoading, mutate } = useSWR(
    hasMore || page === 1 ? url : null,
    (url: string) => apiFetch(url, { withCredentials: true }).then((res) => res.json()),
    {
      revalidateOnFocus: false,
      revalidateIfStale: false
    }
  )

  // Process fetched data
  useEffect(() => {
    if (data?.success && data?.details?.items) {
      const newItems = Array.isArray(data.details.items) ? data.details.items : []

      if (page === 1) {
        setItems(newItems)
      } else {
        setItems(prev => [...prev, ...newItems])
      }

      // Check if there are more items to load based on pagination info
      const hasMorePages = data.details.page < data.details.pages
      setHasMore(hasMorePages)
      setIsLoadingMore(false)
    }
  }, [data, page, limit])

  // Set up IntersectionObserver for infinite scroll
  useEffect(() => {
    const sentinel = sentinelRef.current
    if (!sentinel || !hasMore) return

    const observer = new IntersectionObserver(
      (entries) => {
        const [entry] = entries
        if (entry.isIntersecting && !isLoading && !isLoadingMore) {
          setIsLoadingMore(true)
          setPage(prev => prev + 1)
        }
      },
      {
        root: null,
        rootMargin: '100px',
        threshold: 0.1
      }
    )

    observer.observe(sentinel)

    return () => {
      observer.disconnect()
    }
  }, [hasMore, isLoading, isLoadingMore, items])

  // Refresh function to reset and reload
  const refresh = useCallback(() => {
    setItems([])
    setPage(1)
    setHasMore(true)
    mutate()
  }, [mutate])

  return {
    items,
    isLoading: isLoading && page === 1,
    isLoadingMore,
    error: error || null,
    hasMore,
    sentinelRef,
    refresh
  }
}
